import type { ReadinessLocale } from '../i18n/readinessLocale';
import { getGlossaryLocaleEntry } from '../i18n/glossaryLocale';

export interface GlossaryTerm {
  id: string;
  term: string;
  plain: string;
  /** Optional everyday example for MSME owners. */
  example?: string;
}

export const GLOSSARY_TERMS: GlossaryTerm[] = [
  {
    id: 'gst',
    term: 'GST',
    plain: 'Goods and Services Tax registration number — your 15-character tax ID used on invoices.',
  },
  {
    id: 'pan',
    term: 'PAN',
    plain: 'Permanent Account Number — your 10-character income-tax ID issued by the Income Tax Department.',
  },
  {
    id: 'udyam',
    term: 'UDYAM',
    plain: 'Udyam registration number — the official MSME registration ID from the Udyam portal.',
  },
  {
    id: 'brsr',
    term: 'BRSR',
    plain: 'Business Responsibility and Sustainability Report — a yearly report some Indian companies must file with SEBI.',
  },
  {
    id: 'pat',
    term: 'PAT',
    plain: 'Perform, Achieve and Trade — a BEE scheme that sets energy-saving targets for large industries.',
  },
  {
    id: 'icm',
    term: 'ICM',
    plain: 'Indian Carbon Market — where companies can buy or sell carbon credits in India.',
  },
  {
    id: 'adeetie',
    term: 'ADEETIE',
    plain: 'A BEE programme that offers cheaper loans when MSMEs invest in energy-saving equipment.',
  },
  {
    id: 'gift',
    term: 'GIFT',
    plain: 'Government green finance schemes that help MSMEs fund cleaner equipment or processes.',
  },
  {
    id: 'cbam',
    term: 'CBAM',
    plain: 'EU Carbon Border Adjustment Mechanism — extra carbon rules for goods exported to Europe.',
  },
  {
    id: 'iso14064',
    term: 'ISO 14064',
    plain: 'International standard for measuring and reporting your company\'s greenhouse gas inventory.',
  },
  {
    id: 'cdp',
    term: 'CDP',
    plain: 'Carbon Disclosure Project — a common buyer climate questionnaire for suppliers.',
  },
  {
    id: 'csrd',
    term: 'CSRD',
    plain: 'EU corporate sustainability rules that often require climate data from suppliers.',
  },
  {
    id: 'ecovadis',
    term: 'EcoVadis',
    plain: 'A supplier sustainability rating used by many global buyers.',
  },
  {
    id: 'scope1',
    term: 'Scope 1',
    plain: 'Emissions from fuel you burn on-site — generators, furnaces, and company vehicles.',
    example: 'Diesel for your generator or LPG in production counts here.',
  },
  {
    id: 'scope_1',
    term: 'Scope 1 (Direct)',
    plain: 'Emissions from fuel you burn on-site — generators, furnaces, and company vehicles.',
    example: 'Diesel for your generator or LPG in production counts here.',
  },
  {
    id: 'scope2',
    term: 'Scope 2',
    plain: 'Emissions from electricity you buy from the grid.',
    example: 'Your MSEB or state electricity board bill mainly drives Scope 2.',
  },
  {
    id: 'scope_2',
    term: 'Scope 2 (Electricity)',
    plain: 'Emissions from electricity you buy from the grid.',
    example: 'Your MSEB or state electricity board bill mainly drives Scope 2.',
  },
  {
    id: 'scope3',
    term: 'Scope 3',
    plain: 'Emissions from your supply chain — materials, transport, waste, and other indirect sources.',
    example: 'Steel purchases, courier deliveries, and waste disposal often sit here.',
  },
  {
    id: 'scope_3',
    term: 'Scope 3 (Value chain)',
    plain: 'Emissions from your supply chain — materials, transport, waste, and other indirect sources.',
    example: 'Steel purchases, courier deliveries, and waste disposal often sit here.',
  },
  {
    id: 'tco2e',
    term: 'tCO₂e',
    plain: 'Tonnes of carbon dioxide equivalent — a standard way to express your total greenhouse gas footprint.',
  },
  {
    id: 'carbon_footprint',
    term: 'Carbon footprint',
    plain: 'The total greenhouse gases your business activities produce, usually measured over one year.',
  },
  {
    id: 'carbon_score',
    term: 'Carbon score',
    plain: 'A simple 0–100 rating of how well your business is managing emissions compared to your sector.',
  },
  {
    id: 'ghg',
    term: 'GHG',
    plain: 'Greenhouse gases — gases like CO₂ that trap heat and contribute to climate change.',
  },
  {
    id: 'msme',
    term: 'MSME',
    plain: 'Micro, Small and Medium Enterprise — the official category for small and medium businesses in India.',
  },
  {
    id: 'ocr',
    term: 'Automatic bill reading',
    plain: 'The app reads text from scanned bills and invoices automatically — you do not type amounts by hand.',
    example: 'Upload a photo of your electricity bill; we pull out kWh, amount, and date.',
  },
  {
    id: 'emission_factor',
    term: 'Emission factor',
    plain: 'A standard conversion rate that turns activity data (like kWh or litres of diesel) into CO₂ emissions.',
  },
];

export const getGlossaryTerm = (id: string, locale: ReadinessLocale = 'en'): GlossaryTerm | undefined => {
  const base = GLOSSARY_TERMS.find((t) => t.id === id);
  if (!base) {
    return undefined;
  }
  const localized = getGlossaryLocaleEntry(id, locale);
  if (!localized) {
    return base;
  }
  return {
    ...base,
    term: localized.term ?? base.term,
    plain: localized.plain ?? base.plain,
    example: localized.example ?? base.example,
  };
};
